import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Input,
  Label,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  FormGroup,
  Collapse
} from 'reactstrap';
import Select from 'react-select';
import ApiService from '../services/ApiService';
import { toast } from 'react-toastify';

// Object categories for dropdown
const OBJECT_CATEGORIES = [
  { value: 'team', label: 'Team' },
  { value: 'stadium', label: 'Stadium' },
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'officials', label: 'Officials' },
  { value: 'person', label: 'Person' },
  { value: 'product', label: 'Product' },
  { value: 'signage', label: 'Signage' }
];

// Theme-aware styles for react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--background-secondary)',
    borderColor: state.isFocused ? 'var(--accent-color)' : 'var(--border-color)',
    color: 'var(--text-primary)',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(var(--accent-color-rgb), 0.25)' : 'none',
    '&:hover': {
      borderColor: 'var(--accent-color)'
    }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--background-primary)',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--card-shadow)',
    zIndex: 9999
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--accent-color)'
      : state.isFocused
        ? 'var(--hover-color)'
        : 'transparent',
    color: state.isSelected
      ? 'var(--background-primary)'
      : 'var(--text-primary)',
    '&:hover': {
      backgroundColor: state.isSelected
        ? 'var(--accent-color)'
        : 'var(--hover-color)'
    }
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--text-primary)'
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--text-primary)'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--text-secondary)'
  })
};

const AdminSponsorObjects = () => {
  // Data state
  const [sponsorObjects, setSponsorObjects] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [brandClasses, setBrandClasses] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRightsholder, setFilterRightsholder] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);

  // Selection state
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectMappings, setObjectMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Modal state - Sponsor Object
  const [isObjectModalOpen, setIsObjectModalOpen] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [objectForm, setObjectForm] = useState({
    rightsholder_id: '',
    object_category: '',
    object_id: '',
    object_agent_description: '',
    team_id: '',
    placement_category: '',
    display_name: '',
    active: true
  });

  // Modal state - Brand Mapping
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [mappingForm, setMappingForm] = useState({
    brand_class: '',
    mapped_tenant_id: '',
    mapped_contract_id: '',
    split_type: '',
    split_value: '',
    active: true
  });
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  // Delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState(null);

  // Visual Examples state (placement examples: brand on sponsor object)
  const [objectExamples, setObjectExamples] = useState([]);
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [isExampleModalOpen, setIsExampleModalOpen] = useState(false);
  const [exampleForm, setExampleForm] = useState({
    brand_class: '',
    description: ''
  });
  const [exampleFile, setExampleFile] = useState(null);
  const [uploadingExample, setUploadingExample] = useState(false);
  const exampleFileRef = useRef(null);

  // Brand Examples state
  const [brandExamplesOpen, setBrandExamplesOpen] = useState(false);
  const [selectedBrandClass, setSelectedBrandClass] = useState(null);
  const [selectedBrandRightsholder, setSelectedBrandRightsholder] = useState(null);
  const [brandExamples, setBrandExamples] = useState([]);
  const [loadingBrandExamples, setLoadingBrandExamples] = useState(false);
  const [isBrandExampleModalOpen, setIsBrandExampleModalOpen] = useState(false);
  const [brandExampleFile, setBrandExampleFile] = useState(null);
  const [brandExampleDescription, setBrandExampleDescription] = useState('');
  const [uploadingBrandExample, setUploadingBrandExample] = useState(false);
  const brandExampleFileRef = useRef(null);

  // Initial data load
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    setError('');
    try {
      const [objectsRes, tenantsRes, brandClassesRes] = await Promise.all([
        ApiService.getAdminSponsorObjects(),
        ApiService.getAllTenants(),
        ApiService.getDetectionBrandClasses()
      ]);

      setSponsorObjects(objectsRes.data);
      setTenants(tenantsRes.data);
      setBrandClasses(brandClassesRes.data);
    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch mappings when object is selected
  const fetchMappings = async (objectId) => {
    setLoadingMappings(true);
    try {
      const response = await ApiService.getSponsorObjectMappings(objectId);
      setObjectMappings(response.data);
    } catch (err) {
      toast.error('Failed to load brand mappings');
      setObjectMappings([]);
    } finally {
      setLoadingMappings(false);
    }
  };

  // Fetch contracts for a tenant
  const fetchContracts = async (tenantId) => {
    if (!tenantId) {
      setContracts([]);
      return;
    }
    setLoadingContracts(true);
    try {
      const response = await ApiService.getTenantContracts(tenantId);
      setContracts(response.data);
    } catch (err) {
      console.error('Failed to load contracts:', err);
      setContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  // Filter sponsor objects
  const filteredObjects = useMemo(() => {
    let filtered = sponsorObjects;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(obj =>
        obj.display_name.toLowerCase().includes(search) ||
        obj.object_id.toLowerCase().includes(search)
      );
    }

    if (filterRightsholder) {
      filtered = filtered.filter(obj => obj.rightsholder_id === filterRightsholder.value);
    }

    if (filterCategory) {
      filtered = filtered.filter(obj => obj.object_category === filterCategory.value);
    }

    return filtered;
  }, [sponsorObjects, searchTerm, filterRightsholder, filterCategory]);

  // Tenant options for dropdown
  const tenantOptions = useMemo(() => {
    return tenants.map(t => ({
      value: t.id,
      label: t.name
    }));
  }, [tenants]);

  // Brand class options for dropdown
  const brandClassOptions = useMemo(() => {
    return brandClasses.map(bc => ({
      value: bc.class,
      label: bc.brand_name ? `${bc.class} (${bc.brand_name})` : bc.class
    }));
  }, [brandClasses]);

  // Validate split totals per brand_class
  const splitValidationErrors = useMemo(() => {
    const errors = [];
    const byBrand = {};
    objectMappings.forEach(m => {
      if (!byBrand[m.brand_class]) byBrand[m.brand_class] = [];
      byBrand[m.brand_class].push(m);
    });

    for (const [brandClass, mappings] of Object.entries(byBrand)) {
      if (mappings.length <= 1) continue;

      const splitTypes = [...new Set(mappings.map(m => m.split_type).filter(Boolean))];
      if (splitTypes.length === 0) {
        errors.push(`Brand "${brandClass}" has multiple tenants but no split type defined.`);
      } else if (splitTypes.length > 1) {
        errors.push(`Brand "${brandClass}" has mixed split types — all mappings must use the same type.`);
      } else if (splitTypes[0] === 'percentage') {
        const total = mappings.reduce((sum, m) => sum + (parseFloat(m.split_value) || 0), 0);
        if (Math.abs(total - 100) > 0.01) {
          errors.push(`Brand "${brandClass}" percentages sum to ${total}% — must equal 100%.`);
        }
      }
    }
    return errors;
  }, [objectMappings]);

  // Contract options for dropdown
  const contractOptions = useMemo(() => {
    return contracts.map(c => ({
      value: c.id,
      label: c.label || `${c.category || 'Contract'} (${c.start_date} → ${c.end_date})`
    }));
  }, [contracts]);

  // Handle object selection
  const handleSelectObject = (obj) => {
    if (selectedObject?.id === obj.id) {
      setSelectedObject(null);
      setObjectMappings([]);
    } else {
      setSelectedObject(obj);
      fetchMappings(obj.id);
    }
  };

  // ==========================================
  // Sponsor Object CRUD
  // ==========================================

  const handleAddObject = () => {
    setEditingObject(null);
    setObjectForm({
      rightsholder_id: '',
      object_category: '',
      object_id: '',
      object_agent_description: '',
      team_id: '',
      placement_category: '',
      display_name: '',
      active: true
    });
    setIsObjectModalOpen(true);
  };

  const handleEditObject = (obj, e) => {
    e.stopPropagation();
    setEditingObject(obj);
    setObjectForm({
      rightsholder_id: obj.rightsholder_id,
      object_category: obj.object_category,
      object_id: obj.object_id,
      object_agent_description: obj.object_agent_description || '',
      team_id: obj.team_id || '',
      placement_category: obj.placement_category || '',
      display_name: obj.display_name,
      active: obj.active
    });
    setIsObjectModalOpen(true);
  };

  const handleDeleteObjectClick = (obj, e) => {
    e.stopPropagation();
    setObjectToDelete(obj);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!objectToDelete) return;

    setSaving(true);
    try {
      await ApiService.deleteSponsorObject(objectToDelete.id);
      toast.success('Sponsor object deleted successfully');
      setSponsorObjects(prev => prev.filter(o => o.id !== objectToDelete.id));
      if (selectedObject?.id === objectToDelete.id) {
        setSelectedObject(null);
        setObjectMappings([]);
      }
    } catch (err) {
      toast.error('Failed to delete sponsor object: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
      setObjectToDelete(null);
    }
  };

  const handleSaveObject = async () => {
    // Validation
    if (!objectForm.rightsholder_id || !objectForm.object_category ||
        !objectForm.object_id || !objectForm.display_name || !objectForm.object_agent_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingObject) {
        const response = await ApiService.updateSponsorObject(editingObject.id, objectForm);
        setSponsorObjects(prev => prev.map(o =>
          o.id === editingObject.id ? response.data : o
        ));
        if (selectedObject?.id === editingObject.id) {
          setSelectedObject(response.data);
        }
        toast.success('Sponsor object updated successfully');
      } else {
        const response = await ApiService.createSponsorObject(objectForm);
        setSponsorObjects(prev => [...prev, response.data]);
        toast.success('Sponsor object created successfully');
      }
      setIsObjectModalOpen(false);
    } catch (err) {
      toast.error('Failed to save sponsor object: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // Brand Mapping CRUD
  // ==========================================

  const handleAddMapping = () => {
    setEditingMapping(null);
    setMappingForm({
      brand_class: '',
      mapped_tenant_id: '',
      mapped_contract_id: '',
      split_type: '',
      split_value: '',
      active: true
    });
    setContracts([]);
    setIsMappingModalOpen(true);
  };

  const handleEditMapping = (mapping) => {
    setEditingMapping(mapping);
    setMappingForm({
      brand_class: mapping.brand_class,
      mapped_tenant_id: mapping.mapped_tenant_id,
      mapped_contract_id: mapping.mapped_contract_id || '',
      split_type: mapping.split_type || '',
      split_value: mapping.split_value != null ? String(mapping.split_value) : '',
      active: mapping.active
    });
    if (mapping.mapped_tenant_id) {
      fetchContracts(mapping.mapped_tenant_id);
    }
    setIsMappingModalOpen(true);
  };

  const handleDeleteMapping = (mappingId) => {
    setObjectMappings(prev => prev.filter(m => m.id !== mappingId));
  };

  const handleMappingTenantChange = (option) => {
    setMappingForm(prev => ({
      ...prev,
      mapped_tenant_id: option ? option.value : '',
      mapped_contract_id: '' // Reset contract when tenant changes
    }));
    if (option) {
      fetchContracts(option.value);
    } else {
      setContracts([]);
    }
  };

  const handleSaveMappingToList = () => {
    if (!mappingForm.brand_class || !mappingForm.mapped_tenant_id) {
      toast.error('Please select brand class and mapped tenant');
      return;
    }

    const tenant = tenants.find(t => t.id === mappingForm.mapped_tenant_id);
    const contract = contracts.find(c => c.id === mappingForm.mapped_contract_id);

    const splitType = mappingForm.split_type || null;
    const splitValue = mappingForm.split_value !== '' ? parseFloat(mappingForm.split_value) : null;

    if (editingMapping) {
      // Update existing mapping in list
      setObjectMappings(prev => prev.map(m =>
        m.id === editingMapping.id
          ? {
              ...m,
              brand_class: mappingForm.brand_class,
              mapped_tenant_id: mappingForm.mapped_tenant_id,
              mapped_contract_id: mappingForm.mapped_contract_id || null,
              split_type: splitType,
              split_value: splitValue,
              active: mappingForm.active,
              mapped_tenant: tenant ? { id: tenant.id, name: tenant.name } : null,
              contract: contract ? { id: contract.id, label: contract.label, category: contract.category } : null
            }
          : m
      ));
    } else {
      // Add new mapping to list (with temporary negative id)
      const newMapping = {
        id: -Date.now(), // Temporary ID for new mappings
        brand_class: mappingForm.brand_class,
        mapped_tenant_id: mappingForm.mapped_tenant_id,
        mapped_contract_id: mappingForm.mapped_contract_id || null,
        split_type: splitType,
        split_value: splitValue,
        active: mappingForm.active,
        mapped_tenant: tenant ? { id: tenant.id, name: tenant.name } : null,
        contract: contract ? { id: contract.id, label: contract.label, category: contract.category } : null
      };
      setObjectMappings(prev => [...prev, newMapping]);
    }

    setIsMappingModalOpen(false);
  };

  const handleSaveAllMappings = async () => {
    if (!selectedObject) return;

    setSaving(true);
    try {
      const mappingsData = objectMappings.map(m => ({
        id: m.id > 0 ? m.id : null, // Send null for new mappings
        brand_class: m.brand_class,
        mapped_tenant_id: m.mapped_tenant_id,
        mapped_contract_id: m.mapped_contract_id,
        split_type: m.split_type || null,
        split_value: m.split_value != null ? parseFloat(m.split_value) : null,
        active: m.active
      }));

      const response = await ApiService.saveSponsorObjectMappings(selectedObject.id, mappingsData);
      setObjectMappings(response.data.mappings);
      toast.success('Brand mappings saved successfully');
    } catch (err) {
      toast.error('Failed to save mappings: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // Visual Examples
  // ==========================================

  const fetchObjectExamples = useCallback(async (objectId) => {
    setLoadingExamples(true);
    try {
      const response = await ApiService.getVisualExamples({
        sponsor_object_id: objectId,
        active_only: true
      });
      setObjectExamples(response.data);
    } catch (err) {
      console.error('Failed to load visual examples:', err);
      setObjectExamples([]);
    } finally {
      setLoadingExamples(false);
    }
  }, []);

  // Fetch examples when object is selected
  useEffect(() => {
    if (selectedObject) {
      fetchObjectExamples(selectedObject.id);
    } else {
      setObjectExamples([]);
    }
  }, [selectedObject, fetchObjectExamples]);

  const handleOpenExampleModal = () => {
    setExampleForm({ brand_class: '', description: '' });
    setExampleFile(null);
    if (exampleFileRef.current) exampleFileRef.current.value = '';
    setIsExampleModalOpen(true);
  };

  const handleUploadExample = async () => {
    if (!exampleFile || !selectedObject) return;

    if (!exampleForm.brand_class) {
      toast.error('Please select a brand class');
      return;
    }

    setUploadingExample(true);
    try {
      const formData = new FormData();
      formData.append('image', exampleFile);
      formData.append('example_type', 'placement');
      formData.append('sponsor_object_id', selectedObject.id);
      formData.append('brand_class', exampleForm.brand_class);
      if (exampleForm.description) formData.append('description', exampleForm.description);
      if (selectedObject.rightsholder_id) formData.append('rightsholder_id', selectedObject.rightsholder_id);

      const response = await ApiService.uploadVisualExample(formData);
      setObjectExamples(prev => [...prev, response.data]);
      setIsExampleModalOpen(false);
      toast.success('Visual example uploaded');
    } catch (err) {
      toast.error('Failed to upload example: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingExample(false);
    }
  };

  const handleDeleteExample = async (exampleId) => {
    try {
      await ApiService.deleteVisualExample(exampleId);
      setObjectExamples(prev => prev.filter(e => e.id !== exampleId));
      toast.success('Example deleted');
    } catch (err) {
      toast.error('Failed to delete example');
    }
  };

  // ==========================================
  // Brand Examples
  // ==========================================

  const fetchBrandExamples = useCallback(async () => {
    if (!selectedBrandClass) {
      setBrandExamples([]);
      return;
    }
    setLoadingBrandExamples(true);
    try {
      const params = {
        example_type: 'brand',
        brand_class: selectedBrandClass.value,
        active_only: true
      };
      if (selectedBrandRightsholder) {
        params.rightsholder_id = selectedBrandRightsholder.value;
      }
      const response = await ApiService.getVisualExamples(params);
      setBrandExamples(response.data);
    } catch (err) {
      console.error('Failed to load brand examples:', err);
      setBrandExamples([]);
    } finally {
      setLoadingBrandExamples(false);
    }
  }, [selectedBrandClass, selectedBrandRightsholder]);

  useEffect(() => {
    fetchBrandExamples();
  }, [fetchBrandExamples]);

  const handleUploadBrandExample = async () => {
    if (!brandExampleFile || !selectedBrandClass) return;

    setUploadingBrandExample(true);
    try {
      const formData = new FormData();
      formData.append('image', brandExampleFile);
      formData.append('example_type', 'brand');
      formData.append('brand_class', selectedBrandClass.value);
      if (brandExampleDescription) formData.append('description', brandExampleDescription);
      if (selectedBrandRightsholder) formData.append('rightsholder_id', selectedBrandRightsholder.value);

      const response = await ApiService.uploadVisualExample(formData);
      setBrandExamples(prev => [...prev, response.data]);
      setIsBrandExampleModalOpen(false);
      setBrandExampleFile(null);
      setBrandExampleDescription('');
      toast.success('Brand example uploaded');
    } catch (err) {
      toast.error('Failed to upload brand example: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingBrandExample(false);
    }
  };

  const handleDeleteBrandExample = async (exampleId) => {
    try {
      await ApiService.deleteVisualExample(exampleId);
      setBrandExamples(prev => prev.filter(e => e.id !== exampleId));
      toast.success('Brand example deleted');
    } catch (err) {
      toast.error('Failed to delete brand example');
    }
  };

  // ==========================================
  // Render
  // ==========================================

  if (loading) {
    return (
      <Row>
        <Col xs={12}>
          <Card>
            <CardBody className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-3" style={{ color: 'var(--text-primary)' }}>Loading sponsor objects...</p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {/* Brand Examples Section */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card>
            <CardBody>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => setBrandExamplesOpen(!brandExamplesOpen)}
              >
                <h5 className="card-title mb-0" style={{ color: 'var(--text-primary)' }}>
                  <i className={`fas fa-chevron-${brandExamplesOpen ? 'down' : 'right'} me-2`}></i>
                  Brand Visual Examples
                </h5>
                <small style={{ color: 'var(--text-secondary)' }}>
                  Upload reference images of brand logos for AI detection
                </small>
              </div>
              <Collapse isOpen={brandExamplesOpen}>
                <div className="mt-3">
                  <Row className="mb-3">
                    <Col md={5}>
                      <Label style={{ color: 'var(--text-primary)' }}>Brand Class</Label>
                      <Select
                        options={brandClassOptions}
                        value={selectedBrandClass}
                        onChange={setSelectedBrandClass}
                        placeholder="Select a brand..."
                        isClearable
                        styles={customSelectStyles}
                      />
                    </Col>
                    <Col md={5}>
                      <Label style={{ color: 'var(--text-primary)' }}>Rightsholder (optional)</Label>
                      <Select
                        options={tenantOptions}
                        value={selectedBrandRightsholder}
                        onChange={setSelectedBrandRightsholder}
                        placeholder="Global (all rightsholders)..."
                        isClearable
                        styles={customSelectStyles}
                      />
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Button
                        color="primary"
                        size="sm"
                        disabled={!selectedBrandClass}
                        onClick={() => {
                          setBrandExampleFile(null);
                          setBrandExampleDescription('');
                          if (brandExampleFileRef.current) brandExampleFileRef.current.value = '';
                          setIsBrandExampleModalOpen(true);
                        }}
                      >
                        <i className="fas fa-upload me-1"></i> Upload
                      </Button>
                    </Col>
                  </Row>

                  {!selectedBrandClass ? (
                    <p style={{ color: 'var(--text-secondary)' }}>Select a brand to view and manage its visual examples.</p>
                  ) : loadingBrandExamples ? (
                    <div className="text-center py-3">
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : brandExamples.length === 0 ? (
                    <Alert color="info">
                      No examples for this brand yet. Upload reference images to help AI identify this logo.
                    </Alert>
                  ) : (
                    <div className="d-flex flex-wrap gap-3">
                      {brandExamples.map(example => (
                        <div
                          key={example.id}
                          style={{
                            position: 'relative',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            width: '150px'
                          }}
                        >
                          <img
                            src={example.image_url}
                            alt={example.description || 'Brand example'}
                            style={{ width: '150px', height: '120px', objectFit: 'cover' }}
                          />
                          <div style={{ padding: '6px 8px' }}>
                            <small style={{ color: 'var(--text-secondary)', display: 'block' }}>
                              {example.description || 'No description'}
                            </small>
                            {example.rightsholder && (
                              <Badge color="secondary" className="mt-1" style={{ fontSize: '0.65rem' }}>
                                {example.rightsholder.name}
                              </Badge>
                            )}
                          </div>
                          <Button
                            color="danger"
                            size="sm"
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              padding: '2px 6px',
                              fontSize: '0.7rem',
                              opacity: 0.8
                            }}
                            onClick={() => handleDeleteBrandExample(example.id)}
                          >
                            <i className="fas fa-times"></i>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Collapse>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0" style={{ color: 'var(--text-primary)' }}>
                  Sponsor Objects Management
                </h5>
                <Button color="primary" onClick={handleAddObject}>
                  <i className="fas fa-plus me-2"></i>
                  Add Sponsor Object
                </Button>
              </div>

              {error && <Alert color="danger">{error}</Alert>}

              {/* Filters */}
              <Row className="mb-4">
                <Col md={4}>
                  <Label style={{ color: 'var(--text-primary)' }}>Search</Label>
                  <Input
                    type="text"
                    placeholder="Search by name or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Label style={{ color: 'var(--text-primary)' }}>Rightsholder</Label>
                  <Select
                    options={tenantOptions}
                    value={filterRightsholder}
                    onChange={setFilterRightsholder}
                    placeholder="All rightsholders..."
                    isClearable
                    styles={customSelectStyles}
                  />
                </Col>
                <Col md={4}>
                  <Label style={{ color: 'var(--text-primary)' }}>Category</Label>
                  <Select
                    options={OBJECT_CATEGORIES}
                    value={filterCategory}
                    onChange={setFilterCategory}
                    placeholder="All categories..."
                    isClearable
                    styles={customSelectStyles}
                  />
                </Col>
              </Row>

              {/* Sponsor Objects Table */}
              {filteredObjects.length === 0 ? (
                <Alert color="info">
                  No sponsor objects found. {sponsorObjects.length > 0 ? 'Try adjusting your filters.' : 'Click "Add Sponsor Object" to create one.'}
                </Alert>
              ) : (
                <Table responsive hover className="align-middle">
                  <thead>
                    <tr>
                      <th style={{ color: 'var(--text-primary)' }}>Display Name</th>
                      <th style={{ color: 'var(--text-primary)' }}>Rightsholder</th>
                      <th style={{ color: 'var(--text-primary)' }}>Category</th>
                      <th style={{ color: 'var(--text-primary)' }}>Object ID</th>
                      <th style={{ color: 'var(--text-primary)' }}>Mappings</th>
                      <th style={{ color: 'var(--text-primary)' }}>Status</th>
                      <th style={{ color: 'var(--text-primary)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredObjects.map(obj => (
                      <tr
                        key={obj.id}
                        onClick={() => handleSelectObject(obj)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedObject?.id === obj.id ? 'var(--hover-color)' : 'transparent'
                        }}
                      >
                        <td style={{ color: 'var(--text-primary)' }}>
                          <strong>{obj.display_name}</strong>
                        </td>
                        <td style={{ color: 'var(--text-secondary)' }}>
                          {obj.rightsholder?.name || '-'}
                        </td>
                        <td>
                          <Badge color="secondary">
                            {OBJECT_CATEGORIES.find(c => c.value === obj.object_category)?.label || obj.object_category}
                          </Badge>
                        </td>
                        <td style={{ color: 'var(--text-secondary)' }}>
                          <code>{obj.object_id}</code>
                        </td>
                        <td>
                          <Badge color={obj.brand_mappings?.length > 0 ? 'info' : 'secondary'}>
                            {obj.brand_mappings?.length || 0}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={obj.active ? 'success' : 'warning'}>
                            {obj.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            color="info"
                            size="sm"
                            className="me-2"
                            onClick={(e) => handleEditObject(obj, e)}
                          >
                            Edit
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={(e) => handleDeleteObjectClick(obj, e)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Brand Mappings Section - shown when object is selected */}
      {selectedObject && (
        <Row className="mt-4">
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ color: 'var(--text-primary)' }}>
                      Brand Mappings for: {selectedObject.display_name}
                    </h5>
                    <small style={{ color: 'var(--text-secondary)' }}>
                      Map detected brand logos to sponsor tenants
                    </small>
                  </div>
                  <div>
                    <Button color="secondary" size="sm" className="me-2" onClick={() => {
                      setSelectedObject(null);
                      setObjectMappings([]);
                    }}>
                      Close
                    </Button>
                    <Button color="primary" size="sm" onClick={handleAddMapping}>
                      <i className="fas fa-plus me-1"></i>
                      Add Brand
                    </Button>
                  </div>
                </div>

                {loadingMappings ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" size="sm" />
                    <span className="ms-2" style={{ color: 'var(--text-secondary)' }}>Loading mappings...</span>
                  </div>
                ) : objectMappings.length === 0 ? (
                  <Alert color="info">
                    No brand mappings configured yet. Click "Add Brand" to create one.
                  </Alert>
                ) : (
                  <>
                    <Table responsive hover className="align-middle">
                      <thead>
                        <tr>
                          <th style={{ color: 'var(--text-primary)' }}>Brand Class</th>
                          <th style={{ color: 'var(--text-primary)' }}>Mapped Tenant</th>
                          <th style={{ color: 'var(--text-primary)' }}>Exposure Split</th>
                          <th style={{ color: 'var(--text-primary)' }}>Contract</th>
                          <th style={{ color: 'var(--text-primary)' }}>Status</th>
                          <th style={{ color: 'var(--text-primary)' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {objectMappings.map(mapping => (
                          <tr key={mapping.id}>
                            <td>
                              <Badge color="primary">{mapping.brand_class}</Badge>
                            </td>
                            <td style={{ color: 'var(--text-primary)' }}>
                              {mapping.mapped_tenant?.name || '-'}
                            </td>
                            <td style={{ color: 'var(--text-secondary)' }}>
                              {mapping.split_type === 'seconds'
                                ? `${mapping.split_value}s / game`
                                : mapping.split_type === 'percentage'
                                  ? `${mapping.split_value}%`
                                  : <span style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>100%</span>}
                            </td>
                            <td style={{ color: 'var(--text-secondary)' }}>
                              {mapping.contract?.label || mapping.contract?.category || '-'}
                            </td>
                            <td>
                              <Badge color={mapping.active ? 'success' : 'warning'}>
                                {mapping.active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                color="info"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditMapping(mapping)}
                              >
                                Edit
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleDeleteMapping(mapping.id)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {splitValidationErrors.length > 0 && (
                      <Alert color="danger" className="mt-3">
                        <strong>Split configuration issues:</strong>
                        <ul className="mb-0 mt-1">
                          {splitValidationErrors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}

                    <div className="text-end mt-3">
                      <Button
                        color="success"
                        onClick={handleSaveAllMappings}
                        disabled={saving || splitValidationErrors.length > 0}
                      >
                        {saving && <Spinner size="sm" className="me-2" />}
                        Save All Mappings
                      </Button>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Visual Examples Section - shown when object is selected */}
      {selectedObject && (
        <Row className="mt-4">
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ color: 'var(--text-primary)' }}>
                      Visual Examples for: {selectedObject.display_name}
                    </h5>
                    <small style={{ color: 'var(--text-secondary)' }}>
                      Reference images of brands placed on this object, sent to AI for improved detection
                    </small>
                  </div>
                  <Button color="primary" size="sm" onClick={handleOpenExampleModal}>
                    <i className="fas fa-upload me-1"></i> Upload Example
                  </Button>
                </div>

                {loadingExamples ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" size="sm" />
                    <span className="ms-2" style={{ color: 'var(--text-secondary)' }}>Loading examples...</span>
                  </div>
                ) : objectExamples.length === 0 ? (
                  <Alert color="info">
                    No visual examples yet. Upload images showing brands placed on this object to improve AI detection.
                  </Alert>
                ) : (
                  <div className="d-flex flex-wrap gap-3">
                    {objectExamples.map(example => (
                      <div
                        key={example.id}
                        style={{
                          position: 'relative',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          width: '180px'
                        }}
                      >
                        <img
                          src={example.image_url}
                          alt={example.description || 'Visual example'}
                          style={{ width: '180px', height: '140px', objectFit: 'cover' }}
                        />
                        <div style={{ padding: '6px 8px' }}>
                          <Badge color="primary" className="mb-1">{example.brand_class}</Badge>
                          <small style={{ color: 'var(--text-secondary)', display: 'block' }}>
                            {example.description || 'No description'}
                          </small>
                        </div>
                        <Button
                          color="danger"
                          size="sm"
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            padding: '2px 6px',
                            fontSize: '0.7rem',
                            opacity: 0.8
                          }}
                          onClick={() => handleDeleteExample(example.id)}
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Sponsor Object Modal */}
      <Modal isOpen={isObjectModalOpen} toggle={() => setIsObjectModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsObjectModalOpen(false)}>
          {editingObject ? 'Edit Sponsor Object' : 'Add Sponsor Object'}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Display Name *</Label>
                <Input
                  type="text"
                  value={objectForm.display_name}
                  onChange={(e) => setObjectForm(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="e.g., Rosenborg Home Jersey"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Rightsholder *</Label>
                <Select
                  options={tenantOptions}
                  value={tenantOptions.find(t => t.value === objectForm.rightsholder_id) || null}
                  onChange={(option) => setObjectForm(prev => ({ ...prev, rightsholder_id: option?.value || '' }))}
                  placeholder="Select rightsholder..."
                  styles={customSelectStyles}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Category *</Label>
                <Select
                  options={OBJECT_CATEGORIES}
                  value={OBJECT_CATEGORIES.find(c => c.value === objectForm.object_category) || null}
                  onChange={(option) => setObjectForm(prev => ({ ...prev, object_category: option?.value || '' }))}
                  placeholder="Select category..."
                  styles={customSelectStyles}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Object ID *</Label>
                <Input
                  type="text"
                  value={objectForm.object_id}
                  onChange={(e) => setObjectForm(prev => ({ ...prev, object_id: e.target.value }))}
                  placeholder="e.g., rosenborg_jersey_home"
                />
                <small className="form-text" style={{ color: 'var(--text-secondary)' }}>
                  Unique identifier used in detection output
                </small>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Agent Description *</Label>
            <Input
              type="textarea"
              rows={6}
              value={objectForm.object_agent_description}
              onChange={(e) => setObjectForm(prev => ({ ...prev, object_agent_description: e.target.value }))}
              placeholder="Visual description for LLM to help identify this object..."
            />
            <small className="form-text" style={{ color: 'var(--text-secondary)' }}>
              Description used by AI to identify this object in images/video
            </small>
          </FormGroup>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Team ID (optional)</Label>
                <Input
                  type="text"
                  value={objectForm.team_id}
                  onChange={(e) => setObjectForm(prev => ({ ...prev, team_id: e.target.value }))}
                  placeholder="e.g., rosenborg"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>Placement Category (optional)</Label>
                <Input
                  type="text"
                  value={objectForm.placement_category}
                  onChange={(e) => setObjectForm(prev => ({ ...prev, placement_category: e.target.value }))}
                  placeholder="e.g., athlete_jersey_front"
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup check>
            <Input
              type="checkbox"
              id="object-active"
              checked={objectForm.active}
              onChange={(e) => setObjectForm(prev => ({ ...prev, active: e.target.checked }))}
            />
            <Label check for="object-active" style={{ color: 'var(--text-primary)' }}>
              Active
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsObjectModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveObject} disabled={saving}>
            {saving && <Spinner size="sm" className="me-2" />}
            {editingObject ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Brand Mapping Modal */}
      <Modal isOpen={isMappingModalOpen} toggle={() => setIsMappingModalOpen(false)}>
        <ModalHeader toggle={() => setIsMappingModalOpen(false)}>
          {editingMapping ? 'Edit Brand Mapping' : 'Add Brand Mapping'}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Brand Class *</Label>
            <Select
              options={brandClassOptions}
              value={brandClassOptions.find(bc => bc.value === mappingForm.brand_class) || null}
              onChange={(option) => setMappingForm(prev => ({ ...prev, brand_class: option?.value || '' }))}
              placeholder="Select brand class..."
              styles={customSelectStyles}
            />
            <small className="form-text" style={{ color: 'var(--text-secondary)' }}>
              The brand/logo class detected by the model
            </small>
          </FormGroup>

          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Mapped Tenant *</Label>
            <Select
              options={tenantOptions}
              value={tenantOptions.find(t => t.value === mappingForm.mapped_tenant_id) || null}
              onChange={handleMappingTenantChange}
              placeholder="Select tenant to attribute..."
              styles={customSelectStyles}
            />
            <small className="form-text" style={{ color: 'var(--text-secondary)' }}>
              The sponsor tenant this detection should be attributed to
            </small>
          </FormGroup>

          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Contract (optional)</Label>
            <Select
              options={contractOptions}
              value={contractOptions.find(c => c.value === mappingForm.mapped_contract_id) || null}
              onChange={(option) => setMappingForm(prev => ({ ...prev, mapped_contract_id: option?.value || '' }))}
              placeholder={loadingContracts ? 'Loading...' : 'Select contract...'}
              isDisabled={loadingContracts || !mappingForm.mapped_tenant_id}
              isClearable
              styles={customSelectStyles}
            />
            <small className="form-text" style={{ color: 'var(--text-secondary)' }}>
              Link to a specific sponsorship contract
            </small>
          </FormGroup>

          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Exposure Split (optional)</Label>
            <Row>
              <Col md={6}>
                <Select
                  options={[
                    { value: '', label: 'No split (100%)' },
                    { value: 'seconds', label: 'Seconds per game' },
                    { value: 'percentage', label: 'Percentage of coverage' }
                  ]}
                  value={
                    mappingForm.split_type
                      ? { value: mappingForm.split_type, label: mappingForm.split_type === 'seconds' ? 'Seconds per game' : 'Percentage of coverage' }
                      : { value: '', label: 'No split (100%)' }
                  }
                  onChange={(option) => setMappingForm(prev => ({
                    ...prev,
                    split_type: option?.value || '',
                    split_value: option?.value ? prev.split_value : ''
                  }))}
                  styles={customSelectStyles}
                />
              </Col>
              {mappingForm.split_type && (
                <Col md={6}>
                  <Input
                    type="number"
                    min="0"
                    max={mappingForm.split_type === 'percentage' ? 100 : undefined}
                    step={mappingForm.split_type === 'percentage' ? 0.1 : 1}
                    value={mappingForm.split_value}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, split_value: e.target.value }))}
                    placeholder={mappingForm.split_type === 'seconds' ? 'e.g. 120' : 'e.g. 90'}
                  />
                </Col>
              )}
            </Row>
            <small className="form-text" style={{ color: 'var(--text-secondary)' }}>
              {mappingForm.split_type === 'seconds'
                ? 'Number of seconds this tenant owns per game on this object'
                : mappingForm.split_type === 'percentage'
                  ? 'Percentage of total coverage attributed to this tenant (all mappings for this brand must sum to 100%)'
                  : 'Leave as "No split" if this tenant owns 100% of the exposure on this object'}
            </small>
          </FormGroup>

          <FormGroup check>
            <Input
              type="checkbox"
              id="mapping-active"
              checked={mappingForm.active}
              onChange={(e) => setMappingForm(prev => ({ ...prev, active: e.target.checked }))}
            />
            <Label check for="mapping-active" style={{ color: 'var(--text-primary)' }}>
              Active
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsMappingModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveMappingToList}>
            {editingMapping ? 'Update' : 'Add'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={() => setIsDeleteModalOpen(false)}>
        <ModalHeader toggle={() => setIsDeleteModalOpen(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p style={{ color: 'var(--text-primary)' }}>
            Are you sure you want to delete the sponsor object "{objectToDelete?.display_name}"?
          </p>
          <p style={{ color: 'var(--text-secondary)' }}>
            This will also delete all associated brand mappings. This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleConfirmDelete} disabled={saving}>
            {saving && <Spinner size="sm" className="me-2" />}
            Delete
          </Button>
        </ModalFooter>
      </Modal>

      {/* Visual Example Upload Modal (placement: brand on sponsor object) */}
      <Modal isOpen={isExampleModalOpen} toggle={() => setIsExampleModalOpen(false)}>
        <ModalHeader toggle={() => setIsExampleModalOpen(false)}>
          Upload Placement Example for: {selectedObject?.display_name}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Brand Class *</Label>
            <Select
              options={brandClassOptions}
              value={brandClassOptions.find(bc => bc.value === exampleForm.brand_class) || null}
              onChange={(option) => setExampleForm(prev => ({ ...prev, brand_class: option?.value || '' }))}
              placeholder="Select brand shown in this example..."
              styles={customSelectStyles}
            />
            <small style={{ color: 'var(--text-secondary)' }}>
              Which brand logo is visible on this sponsor object?
            </small>
          </FormGroup>

          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Image *</Label>
            <Input
              type="file"
              accept="image/*"
              innerRef={exampleFileRef}
              onChange={(e) => setExampleFile(e.target.files[0] || null)}
            />
            <small style={{ color: 'var(--text-secondary)' }}>Max 10MB. JPG, PNG, or WebP recommended.</small>
          </FormGroup>

          {exampleFile && (
            <div className="mb-3 text-center">
              <img
                src={URL.createObjectURL(exampleFile)}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '1px solid var(--border-color)' }}
              />
            </div>
          )}

          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Description (optional)</Label>
            <Input
              type="text"
              value={exampleForm.description}
              onChange={(e) => setExampleForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="e.g., Nike swoosh on home jersey chest"
            />
            <small style={{ color: 'var(--text-secondary)' }}>
              Sent alongside the image to help AI understand what it shows
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsExampleModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUploadExample} disabled={uploadingExample || !exampleFile}>
            {uploadingExample && <Spinner size="sm" className="me-2" />}
            Upload
          </Button>
        </ModalFooter>
      </Modal>

      {/* Brand Example Upload Modal */}
      <Modal isOpen={isBrandExampleModalOpen} toggle={() => setIsBrandExampleModalOpen(false)}>
        <ModalHeader toggle={() => setIsBrandExampleModalOpen(false)}>
          Upload Brand Example: {selectedBrandClass?.label}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Image *</Label>
            <Input
              type="file"
              accept="image/*"
              innerRef={brandExampleFileRef}
              onChange={(e) => setBrandExampleFile(e.target.files[0] || null)}
            />
            <small style={{ color: 'var(--text-secondary)' }}>Max 10MB. JPG, PNG, or WebP recommended.</small>
          </FormGroup>

          {brandExampleFile && (
            <div className="mb-3 text-center">
              <img
                src={URL.createObjectURL(brandExampleFile)}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '1px solid var(--border-color)' }}
              />
            </div>
          )}

          <FormGroup>
            <Label style={{ color: 'var(--text-primary)' }}>Description (optional)</Label>
            <Input
              type="text"
              value={brandExampleDescription}
              onChange={(e) => setBrandExampleDescription(e.target.value)}
              placeholder="e.g., Nike swoosh on white background"
            />
          </FormGroup>

          {selectedBrandRightsholder && (
            <Alert color="info" className="mb-0">
              This example will be specific to <strong>{selectedBrandRightsholder.label}</strong>.
              Clear the rightsholder filter to upload a global example.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsBrandExampleModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUploadBrandExample} disabled={uploadingBrandExample || !brandExampleFile}>
            {uploadingBrandExample && <Spinner size="sm" className="me-2" />}
            Upload
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AdminSponsorObjects;
